import { Box, FormControl, FormHelperText, Input, CircularProgress, Stack } from '@mui/joy';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { InlineEditButtons } from '@/components/InlineEditButtons';
import { EmojiPickerButton } from '@/components/EmojiPickerButton';
import { useCreateCategoryMutation } from '../api';
import { categorySchema } from '../schema';
import { getCategoryErrorMessage } from '../utils';

export function AddCategoryForm({ onSave, onCancel }) {
  const [createCategory] = useCreateCategoryMutation();
  
  const { register, handleSubmit, formState, reset, setError, watch, setValue } = useForm({
    resolver: yupResolver(categorySchema),
    defaultValues: { name: '', emoji: 'grinning' }
  });

  const { errors, isSubmitting } = formState;
  const nameError = errors.name;
  const emojiError = errors.emoji;
  const emoji = watch('emoji');

  const onSubmit = async (data) => {
    try {
      const result = await createCategory({
        data: {
          name: data.name.trim(),
          emoji: data.emoji
        }
      }).unwrap();
      
      reset();
      onSave(result.data);
    } catch (error) {
      const errorMessage = getCategoryErrorMessage(error, 'create', 'category');
      setError('name', { type: 'server', message: errorMessage });
    }
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  const handleKeyDown = (e) => {
    if (isSubmitting) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(onSubmit)();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 0.5, pr: 1, mt: { xs: '-5px', md: 0 }, borderRadius: 'sm', bgcolor: 'background.level1', width: { xs: '100%', sm: 'auto' }, minWidth: { md: '400px' } }}>
      <FormControl error={!!nameError || !!emojiError} sx={{ flexGrow: 1 }}>
        <Stack direction="row" gap={0.5} alignItems="center">
          <EmojiPickerButton 
            emoji={emoji} 
            onEmojiSelect={(selectedEmoji) => setValue('emoji', selectedEmoji, { shouldValidate: true })} 
            disabled={isSubmitting} 
          />
          <Input
            {...register('name')}
            onKeyDown={handleKeyDown}
            autoFocus
            size="sm"
            disabled={isSubmitting}
            placeholder="Category name"
            sx={{ flexGrow: 1, fontSize: '14px', opacity: isSubmitting ? 0.6 : 1 }}
            endDecorator={isSubmitting ? <CircularProgress size="sm" /> : null}
          />
          <InlineEditButtons onSave={handleSubmit(onSubmit)} onCancel={handleCancel} disabled={isSubmitting} />
        </Stack>
        {(nameError || emojiError) && (
          <FormHelperText sx={{ ml: 0, mt: 0.5 }}>
            {nameError?.message || emojiError?.message}
          </FormHelperText>
        )}
      </FormControl>
    </Box>
  );
}

